import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Box, Group, SimpleGrid, Stack } from '@mantine/core'
import { AppSkeleton } from '../../app/common/ui'
import { IoAdd, IoSearchOutline, IoPeopleOutline, IoChatbubbleOutline } from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { CacheListItemDto } from '../../app/models/cache'
import { AppButton, AppCard, AppInput, AppText, EmptyState } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { canCreateCache } from '../../app/common/constants/reputation'
import { CacheCover, CacheVisibilityBadge, cacheCardHoverSx, resolveCacheCover } from './cacheUi'

const PAGE_MAX = 1040

function CacheCard ({ cache }: { cache: CacheListItemDto }) {
  return (
    <AppCard
      component={Link}
      to={`/caches/${cache.id}`}
      padding={0}
      sx={{
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        background: 'var(--gs-surface)',
        ...cacheCardHoverSx
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CacheCover url={resolveCacheCover(cache)} name={cache.name} seed={cache.id} aspectRatio="16 / 9" radius={0} />
        {(cache.isOfficial || cache.visibility !== 'Public') && (
          <Group spacing={6} sx={{ position: 'absolute', top: 10, right: 10 }}>
            {cache.isOfficial && (
              <Box sx={{ background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: '2px 7px', backdropFilter: 'blur(4px)' }}>
                <AppText size="xs" weight={700} sx={{ color: '#ffce6b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Official</AppText>
              </Box>
            )}
            <CacheVisibilityBadge visibility={cache.visibility} size="xs" hidePublic />
          </Group>
        )}
      </Box>
      <Box p="md">
        <AppText weight={600} lineClamp={1} mb={cache.firearmModelMake ? 2 : 6}>{cache.name}</AppText>

        {cache.firearmModelMake && (
          <AppText size="xs" color="dimmed" mb={6} lineClamp={1}>
            {cache.firearmModelMake} {cache.firearmModelName}
          </AppText>
        )}

        <AppText size="sm" color="dimmed" lineClamp={2} sx={{ minHeight: 40 }}>
          {cache.description || 'A community for fans of this firearm.'}
        </AppText>

        <Group spacing="md" mt="sm">
          <Group spacing={4}>
            <IoPeopleOutline size={13} color="var(--gs-muted)" />
            <AppText size="xs" color="dimmed">{cache.memberCount}</AppText>
          </Group>
          <Group spacing={4}>
            <IoChatbubbleOutline size={13} color="var(--gs-muted)" />
            <AppText size="xs" color="dimmed">{cache.postCount}</AppText>
          </Group>
        </Group>
      </Box>
    </AppCard>
  )
}

export default observer(function CachesPage () {
  const { userStore } = useStore()
  const history = useHistory()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [items, setItems] = useState<CacheListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pageSize = 24

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300)
  }

  useEffect(() => {
    setLoading(true)
    setDisabled(false)
    setPage(1)
    agent.Caches.list({ search: debouncedSearch || undefined, pageNumber: 1, pageSize })
      .then((r) => {
        setItems(r.items)
        setTotalCount(r.totalCount)
      })
      .catch((err: { response?: { status?: number } }) => {
        if (err.response?.status === 403) setDisabled(true)
        else setItems([])
      })
      .finally(() => setLoading(false))
  }, [debouncedSearch])

  const loadMore = () => {
    const next = page + 1
    setLoadingMore(true)
    agent.Caches.list({ search: debouncedSearch || undefined, pageNumber: next, pageSize })
      .then((r) => {
        setItems((prev) => [...prev, ...r.items])
        setTotalCount(r.totalCount)
        setPage(next)
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false))
  }

  if (disabled) {
    return (
      <Box sx={{ padding: '24px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <EmptyState title="Caches are off" message="Enable caches-enabled in Overseer after migration." />
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '20px 24px 48px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
      <Group position="apart" align="flex-end" mb="lg">
        <Box>
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 28, letterSpacing: 0.5, lineHeight: 1.1 }}>
            CACHES
          </AppText>
          <AppText size="sm" color="dimmed" mt={2}>
            Firearm communities — join a fan club or start your own.
          </AppText>
        </Box>
        <Group spacing="xs">
          <AppButton component={Link} to="/polls" variant="default" size="sm">
            Polls
          </AppButton>
          {userStore.isLoggedIn && canCreateCache(userStore.user) && (
            <AppButton component={Link} to="/caches/new" color="ember" size="sm" leftIcon={<IoAdd size={16} />}>
              New cache
            </AppButton>
          )}
        </Group>
      </Group>

      <AppInput
        placeholder="Search caches…"
        aria-label="Search caches"
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.currentTarget.value)}
        icon={<IoSearchOutline size={16} />}
        mb="lg"
        sx={{ maxWidth: 420 }}
      />

      {loading
        ? (
        <SimpleGrid cols={3} spacing="md" breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'xs', cols: 1 }]}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Stack key={i} spacing={0} sx={{ border: '1px solid var(--gs-border)', borderRadius: 12, overflow: 'hidden' }}>
              <AppSkeleton radius={0} sx={{ aspectRatio: '16 / 9', width: '100%' }} />
              <Box p="md">
                <AppSkeleton height={14} width="70%" mb={8} />
                <AppSkeleton height={10} width="40%" mb={12} />
                <AppSkeleton height={10} width="90%" mb={6} />
                <AppSkeleton height={10} width="60%" />
              </Box>
            </Stack>
          ))}
        </SimpleGrid>
          )
        : items.length === 0
          ? (
        <EmptyState
          title={debouncedSearch ? 'No caches match your search' : 'No caches yet'}
          message={debouncedSearch ? 'Try a different search term.' : 'Be the first to start a community.'}
          action={
            !debouncedSearch && userStore.isLoggedIn && canCreateCache(userStore.user)
              ? { label: 'New cache', onClick: () => history.push('/caches/new') }
              : undefined
          }
        />
            )
          : (
        <>
          <SimpleGrid cols={3} spacing="md" breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'xs', cols: 1 }]}>
            {items.map((c) => <CacheCard key={c.id} cache={c} />)}
          </SimpleGrid>
          {items.length < totalCount && (
            <Group position="center" mt="xl">
              <AppButton variant="default" loading={loadingMore} onClick={loadMore}>
                Load more caches
              </AppButton>
            </Group>
          )}
        </>
            )}
    </Box>
  )
})
